import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Page, Spinner, BlockCard, DropdownCard, Button, Select, Tag } from '@trellixio/roaster-coffee';
import { useQuery } from '@tanstack/react-query';
import Locales from 'locales';
import { useAPI } from 'hooks';
import { StatCardsList } from 'components';
import styles from './SuperAdminDashboard.module.css';

const CONST_NONE = 'none';
const CONST_FILTER_LABEL_YEAR = 'year';

const StatsSection = ({ title, data }) => (
  <section>
    <h3 className="content-row">{title}</h3>
    {data ? (
      <div className="items-group wrap xl">
        <StatCardsList statsData={data} />
      </div>
    ) : (
      <Spinner />
    )}
  </section>
);

StatsSection.defaultProps = {
  title: null,
  data: null,
};

StatsSection.propTypes = {
  title: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({})),
};

const SuperAdminDashboard = ({
  queryFilters,
  setQueryFilter,
  yearsArray,
  preSetYear,
  setSearchParams,
  selectedYear,
  setYear,
  showTag,
  setShowTag,
  fetchParams,
}) => {
  const { api } = useAPI();

  const { data: depositsData } = useQuery({
    queryFn: api.list('deposit/stats/default/', fetchParams),
    queryKey: ['deposit/stats/default/', queryFilters],
  });

  const { data: agenciesData } = useQuery({
    queryFn: api.list('agency/stats/default/', fetchParams),
    queryKey: ['agency/stats/default/', queryFilters],
  });

  const { data: pilgrimsDemographicData } = useQuery({
    queryFn: api.list('trip/stats/demographic/', fetchParams),
    queryKey: ['trip/stats/demographic/', queryFilters],
  });

  const { data: pilgrimsPaymentData } = useQuery({
    queryFn: api.list('trip/stats/payment/', fetchParams),
    queryKey: ['trip/stats/payment/', queryFilters],
  });

  const { data: pilgrimsHealthData } = useQuery({
    queryFn: api.list('trip/stats/health/', fetchParams),
    queryKey: ['trip/stats/health/', queryFilters],
  });

  const { data: pilgrimsVisaData } = useQuery({
    queryFn: api.list('trip/stats/visa/', fetchParams),
    queryKey: ['trip/stats/visa/', queryFilters],
  });

  const { data: pilgrimsMiscData } = useQuery({
    queryFn: api.list('trip/stats/misc/', fetchParams),
    queryKey: ['trip/stats/misc/', queryFilters],
  });

  const { data: pilgrimsCategoryData } = useQuery({
    queryFn: api.list('trip/stats/category/', fetchParams),
    queryKey: ['trip/stats/category/', queryFilters],
  });

  const { data: pilgrimsCategoryPaidData } = useQuery({
    queryFn: api.list('trip/stats/trip_category_paid/', fetchParams),
    queryKey: ['trip/stats/trip_category_paid/', queryFilters],
  });

  // we skip first element "Total" because it's not within the stats to display
  const depositsStats = depositsData?.data.data.filter((stats, index) => index > 0);

  const agenciesStats = agenciesData?.data.data;

  const pilgrimsDemographicStats = pilgrimsDemographicData?.data.data;

  const pilgrimsPaymentStats = pilgrimsPaymentData?.data.data;

  const pilgrimsHealthStats = pilgrimsHealthData?.data.data;

  const pilgrimsVisaStats = pilgrimsVisaData?.data.data;

  const pilgrimsMiscStats = pilgrimsMiscData?.data.data;

  const pilgrimsCategory = pilgrimsCategoryData?.data.data;

  const pilgrimsCategoryPaid = pilgrimsCategoryPaidData?.data.data;

  const applyFilters = () => {
    let filters = null;

    if (selectedYear !== CONST_NONE) {
      filters = { [CONST_FILTER_LABEL_YEAR]: selectedYear };
    }

    setQueryFilter(filters);
    setShowTag(true);
  };

  const resetFilters = () => {
    setYear(CONST_NONE);
    setQueryFilter(null);
    setShowTag(false);
  };

  useMemo(() => {
    // set value if in URL and allow the filter to be applied only if the user have the right to apply it
    if (preSetYear !== CONST_NONE) {
      applyFilters();
    }
  }, []);

  useMemo(() => {
    if (preSetYear === null) {
      resetFilters();
    }
  }, [preSetYear]);

  useMemo(() => {
    let params = {};

    // update URL each time the filter is selected
    if (selectedYear !== CONST_NONE) {
      params = { [CONST_FILTER_LABEL_YEAR]: selectedYear };
    }

    if (params) {
      setSearchParams(params);
    }
  }, [selectedYear]);

  return (
    <Page
      title={Locales.dashboard.title}
      size="full"
      className={styles.container}
      secondaryAction={
        <form>
          <DropdownCard>
            <DropdownCard.Trigger>
              <Button variant="secondary">
                <i className="fa-regular fa-bars-filter" /> {Locales.trip.filter.filter}
              </Button>
            </DropdownCard.Trigger>
            <DropdownCard.Dropdown width="large">
              <DropdownCard.Content>
                <form>
                  <div className="content-row">
                    <p className="group-label">{Locales.campaign.dropdown.campaign_year}</p>
                    <Select
                      defaultValue={selectedYear}
                      helpText={Locales.campaign.dropdown.campaign_year_helptext}
                      data={[{ label: Locales.base.button.select, value: CONST_NONE, disabled: true }].concat(
                        yearsArray
                      )}
                      onChange={setYear}
                    />
                  </div>
                </form>
              </DropdownCard.Content>
              <DropdownCard.Actions>
                <Button variant="inline" onClick={resetFilters}>
                  {Locales.base.button.reset}
                </Button>
                <Button color="success" onClick={applyFilters}>
                  {Locales.base.button.validate}
                </Button>
              </DropdownCard.Actions>
            </DropdownCard.Dropdown>
          </DropdownCard>
        </form>
      }
    >
      {showTag && (
        <BlockCard>
          <div className="table-meta items-group">
            <div className="tags-container">
              {queryFilters && Object.prototype.hasOwnProperty.call(queryFilters, CONST_FILTER_LABEL_YEAR) && (
                <Tag
                  onRemove={() => resetFilters()}
                >{`${Locales.campaign.dropdown.campaign_year}: ${selectedYear}`}</Tag>
              )}
            </div>
          </div>
        </BlockCard>
      )}

      <StatsSection
        title={Locales.base.stats.pilgrims}
        data={[...(pilgrimsDemographicStats || []), ...(pilgrimsCategory || []), ...(pilgrimsCategoryPaid || [])]}
      />

      <section>
        <h3 className="content-row">{Locales.base.stats.bank}</h3>
        {depositsStats && pilgrimsPaymentStats ? (
          <div className="items-group wrap xl">
            <StatCardsList statsData={depositsStats} />
            <StatCardsList statsData={pilgrimsPaymentStats} />
          </div>
        ) : (
          <Spinner />
        )}
      </section>

      <StatsSection title={Locales.base.stats.health} data={pilgrimsHealthStats} />

      <StatsSection title={Locales.base.stats.visa} data={pilgrimsVisaStats} />

      <StatsSection title={Locales.base.stats.no_visa_application} data={pilgrimsMiscStats} />

      <StatsSection title={Locales.base.stats.agencies} data={agenciesStats} />
    </Page>
  );
};

SuperAdminDashboard.propTypes = {
  queryFilters: PropTypes.shape({}),
  setQueryFilter: PropTypes.func,
  yearsArray: PropTypes.arrayOf(PropTypes.shape({})),
  preSetYear: PropTypes.string,
  setSearchParams: PropTypes.func,
  selectedYear: PropTypes.string,
  setYear: PropTypes.func,
  showTag: PropTypes.bool,
  setShowTag: PropTypes.func,
  fetchParams: PropTypes.shape({}),
};

SuperAdminDashboard.defaultProps = {
  queryFilters: null,
  setQueryFilter: null,
  yearsArray: null,
  preSetYear: null,
  setSearchParams: null,
  selectedYear: null,
  setYear: null,
  showTag: false,
  setShowTag: null,
  fetchParams: null,
};

export default SuperAdminDashboard;
